import React, { useEffect, useState, useLayoutEffect, useCallback } from 'react'

import { PageProps } from 'gatsby'
import { parseISO, isBefore } from 'date-fns'
import axios from 'axios'
import { NewsField } from 'src/services/articles/articles'
import Layout from 'src/components/Layout'
import PageTitle from 'src/components/PageTitle'
import ListArticles from 'src/components/ListArticles/ListArticles'
import categoriesJSON from 'src/assets/data/categories.json'
import trails from 'src/assets/data/trails.json'
import dataJSON from 'src/assets/data/banners.json'
import carousel from 'src/assets/data/video-carousel.json'
import useWidth from 'src/hooks/useWidth'
import CarouselTimerMobile from 'src/components/CarouselTimer/CarouselTimerMobile'
import subcategoriesJSON from 'src/assets/data/subcategories.json'
import HeroNews, { HeroNewsCategoryItem } from 'src/components/HeroNews/HeroNewsCategory'
import BestContents, { BestContentItem, BestContentsBannerFill } from 'src/components/BestContents'
import SEO from 'src/components/Seo'
import Carousel, { CarouselBannerItem } from 'src/components/Carousel'
import SmallCard, { SmallCardData } from 'src/components/SmallCard'
import CarouselVideos, { CarouselVideosData } from 'src/components/CarouselVideos'
import bestContentJSON from 'src/assets/data/best-content.json'
import Newsletter from 'src/components/Newsletter'
import { NoticiaType } from 'src/components/News'
import { getColors } from 'src/styles/themes'

import Ferramentas from './components/_ferramentas'
import Analise from './components/_analise'

export type Article = {
  id: string
  slug: string
  title: string
  introduction: string
  gridImageUrl: string
  categoryName: string
  publishedAt: string
  updatedAt: string
  shares: number
  spotlight: boolean
  readingTime: number
  subcategoryId: string
  segmentId: string
  categoryId: string
  isFromInset?: boolean
  insetCategory?: string
  homeImage: {
    url: string
    alt: string
  }
}

type AnalyticsData = {
  uuid: string
  count: number
}

type CategoryMeta = {
  id: string
  slug: string
  name: string
  template: string
  icon?: string
  subTitle?: string
}

type SubcategoryMeta = {
  id: string
  slug: string
  name: string
}

/** Item de spotlight compatível com HeroNews e CarouselTimerMobile */
type CategorySpotlightItem = HeroNewsCategoryItem & {
  subtitle: string
  category: HeroNewsCategoryItem['category'] & {
    colors: ReturnType<typeof getColors>
  }
}

type CategorySpotlightData = {
  template: string
  categoryLabel: string
  category: string
  slug: string
  isCategoryTemplate: boolean
  dados: CategorySpotlightItem[]
}

type CategoryProps = {
  pageContext: {
    pageTitle: string
    categoria: string
    allArticles: Article[]
    categoryid: string
    pageSubTitle: string
    icon: string
  }
} & PageProps

const categories = categoriesJSON as CategoryMeta[]
const subcategories = subcategoriesJSON as SubcategoryMeta[]
const bestContentItems = bestContentJSON as BestContentItem[]
const bannerItems = dataJSON as CarouselBannerItem[]
const trailItems = trails as SmallCardData[]

const isCarouselVideosData = (value: unknown): value is CarouselVideosData =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && 'news' in value

const videoCarouselData = isCarouselVideosData(carousel) ? carousel : null

const bannerFill2: BestContentsBannerFill = {
  showCategoryHeader: false,
  template: 'D',
  category: 'recomendacoes',
  categoryLabel: 'Recomendações',
  slug: '/recomendacoes',
  dados: bestContentItems.filter((item) => item.category === 'recomendacoes'),
}

const bannerFill3: BestContentsBannerFill = {
  showCategoryHeader: false,
  template: 'D',
  category: 'analise',
  categoryLabel: 'Análise',
  slug: '/analise',
  dados: bestContentItems.filter((item) => item.category === 'analise'),
}

const CategoryTemplate = ({ pageContext }: CategoryProps) => {
  const [, setIsTablet] = useState(true)
  const allArticlesOrdened = pageContext.allArticles.sort((a, b) => {
    const aDate = parseISO(a.publishedAt)
    const bDate = parseISO(b.publishedAt)
    return isBefore(aDate, bDate) ? 1 : -1
  })

  const [, setMoreShared] = useState<NoticiaType[]>([])
  const [spotLitghtData, setSpotLitghtData] = useState<CategorySpotlightData[]>([])
  const category = categories.find((categoryItem) => categoryItem.slug === pageContext.categoria)
  const breadcrumbPath = [
    {
      text: 'Home',
      link: '/',
    },
    {
      text: category?.name || '',
      link: `/${category?.slug}`,
    },
  ]
  const windowWidth = useWidth(200)
  const [isMobile, setIsMobile] = useState(false)

  useLayoutEffect(() => {
    setIsMobile(windowWidth < 768)
    setIsTablet(windowWidth < 1024)
  }, [windowWidth])

  const normalizeObjectSpotlight = useCallback(
    (articlesInSpotlight: Article[]): CategorySpotlightData => {
      const articlesToSpotLight = articlesInSpotlight.sort((a, b) => {
        const aDate = parseISO(a.publishedAt)
        const bDate = parseISO(b.publishedAt)
        return isBefore(aDate, bDate) ? 1 : -1
      })
      const template = category?.template === 'E' ? 'A' : category?.template || 'A'
      const categorySlug = category?.slug || ''

      return {
        template,
        categoryLabel: category?.name || '',
        category: categorySlug,
        slug: `/${categorySlug}`,
        isCategoryTemplate: true,
        dados: articlesToSpotLight.map((article): CategorySpotlightItem => {
          const articleCategory =
            categories.find((item) => item.id === article.categoryId) || category
          const articleSubcategory = subcategories.find((item) => item.id === article.subcategoryId)
          const resolvedCategorySlug = articleCategory?.slug || categorySlug
          const resolvedColors = getColors(resolvedCategorySlug)

          return {
            showCategoryLabel: articleSubcategory?.name || '',
            category: {
              icon: articleCategory?.icon || '',
              id: articleCategory?.id || '',
              name: articleCategory?.name || '',
              slug: resolvedCategorySlug,
              subTitle: articleCategory?.subTitle || '',
              template: articleCategory?.template || template,
              colors: resolvedColors,
            },
            image: article.homeImage.url,
            alt: article.homeImage.alt || article.title,
            slug: article.slug,
            title: article.title,
            subTitle: article.introduction || '',
            subtitle: article.introduction || '',
            categorySlug: resolvedCategorySlug,
            subcategorySlug: articleSubcategory?.slug || '',
            segmentSlug: '',
            subCategory: articleSubcategory?.name || '',
            isFromInset: article.isFromInset,
            insetCategory: article.insetCategory,
          }
        }),
      }
    },
    [category],
  )

  const HandleScroll = (id: string) => {
    const section = document.getElementById(id)
    const position = section?.offsetTop || 0
    const poss = position - 187
    window.scroll({
      top: poss,
      behavior: 'smooth',
    })
  }

  const clearLocation = (location: string) => {
    const url = new URL(location)
    const id = url.searchParams.get('id')
    return id
  }

  const returnArticlesMoreRead = useCallback(async () => {
    try {
      const articles = pageContext.allArticles
      const response = await axios.post(`${process.env.CMS_API}/news/analyticsdata`)
      const { data } = response
      const resposta = data.reduce((all: NoticiaType[], item: AnalyticsData) => {
        const artigo = articles.find((articleFind) => articleFind.id === item.uuid)
        if (!artigo) {
          return all
        }
        return [...all, NewsField(artigo)]
      }, [])
      setMoreShared(resposta)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }, [pageContext.allArticles])

  useEffect(() => {
    const location = clearLocation(window.location.href)

    if (location) {
      setTimeout(() => HandleScroll(location), 100)
    }

    returnArticlesMoreRead()
  }, [returnArticlesMoreRead])

  useEffect(() => {
    const objectSpotlight = normalizeObjectSpotlight(
      allArticlesOrdened.filter((article) => article.spotlight),
    )
    setSpotLitghtData([objectSpotlight])
  }, [allArticlesOrdened, normalizeObjectSpotlight])

  const heroNewsData: HeroNewsCategoryItem[] = spotLitghtData[0]?.dados.slice(0, 3) || []

  return (
    <Layout pageContext={{ categoria: category?.slug }}>
      <SEO
        title={`${category?.name} · Inter Invest`}
        url={`${process.env.BASE_URL}/${category?.slug}`}
        meta={[
          {
            name: 'description',
            content: category?.name,
          },
          {
            property: 'og:title',
            content: `${category?.name} · Inter Invest`,
          },
          {
            property: 'og:description',
            content: category?.name,
          },
          {
            property: 'og:locale',
            content: 'pt_BR',
          },
          {
            property: 'og:type',
            content: 'website',
          },
          {
            property: 'og:url',
            content: `${process.env.BASE_URL}/${category?.slug}`,
          },
          {
            property: 'og:site_name',
            content: 'Banco Inter',
          },
          {
            property: 'twitter:site',
            content: '@bancointer',
          },
          {
            property: 'twitter:creator',
            content: '@bancointer',
          },
        ]}
      />
      <section>
        <div className="container">
          <div className="row">
            <PageTitle
              breadcrumb={{ path: breadcrumbPath }}
              pageTitle={pageContext.pageTitle}
              pageSubTitle={pageContext.pageSubTitle}
            />
          </div>
        </div>
      </section>
      <section className="pt-md-4 pb-5">
        {pageContext.categoria !== 'aprenda-a-investir' && spotLitghtData.length > 0 && (
          <div className="container">
            {!isMobile ? (
              <HeroNews data={heroNewsData} />
            ) : (
              <CarouselTimerMobile data={spotLitghtData[0]} />
            )}
          </div>
        )}
      </section>
      <Newsletter page={category?.name} category={category?.name} />
      {pageContext.categoryid === 'recomendacoes' && bannerFill2.dados.length > 2 && (
        <section className="pt-5 pb-4">
          <div className="container">
            <BestContents bannerFill={bannerFill2} />
          </div>
        </section>
      )}
      {pageContext.categoryid === '1f3a9c83-9600-4620-a28e-fdd9727cdfe7' && <Ferramentas />}
      {pageContext.categoria === 'analise' && (
        <>
          <section className="mb-5">
            <Analise />
          </section>
          {bannerFill3.dados.length >= 3 && (
            <section className="mb-5">
              <div className="container">
                <BestContents bannerFill={bannerFill3} />
              </div>
            </section>
          )}
        </>
      )}
      {pageContext.categoria === 'aprenda-a-investir' && (
        <>
          {bannerItems.length > 0 && <Carousel data={bannerItems} />}
          {trailItems.length > 0 && (
            <div className="mt-5">
              {trailItems.map((item) => (
                <div key={item.id || item.title}>
                  <SmallCard data={item} />
                </div>
              ))}
            </div>
          )}
          {videoCarouselData?.id && <CarouselVideos data={videoCarouselData} />}
        </>
      )}
      {pageContext.categoria !== 'aprenda-a-investir' && (
        <ListArticles articles={allArticlesOrdened} />
      )}
    </Layout>
  )
}

export default CategoryTemplate
